import { type RootState } from "../redux/store";
import { requestFullProductData } from "../redux/products/actions";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { selectProductDescriptor } from "../redux/products/productsSelectors";
import { useEffect, useMemo } from "react";
import {
  isKITProduct,
  type PackagingInformationEntry,
  type ProductData,
  type WarehouseStockData,
} from "../models/ProductData";
import { INVALID_PACKAGE_CODE } from "../utils/constants";
import { toMapFn } from "../utils/reducers";
import { getStore } from "~/redux/getStore";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";

/**
 * Retrieve the current product descriptor from Redux store.
 * This function does not subscribe to changes in store, so please use it only inside
 * callbacks, not inside a component.
 *
 * For usage inside of a component, please use the useProductDescriptor hook.
 */

function getProductDescriptor(
  productCode: string,
  priceReference: string,
): {
  current: ProductDescriptor;
  promise: undefined;
};
function getProductDescriptor(
  productCode: string,
  priceReference: string,
  params: { fetch: true; forceFetchBasicData?: boolean },
): {
  current: ProductDescriptor;
  promise: Promise<ProductDescriptor>;
};
function getProductDescriptor(
  productCode: string,
  priceReference: string,
  params: { fetch: false; forceFetchBasicData?: boolean },
): {
  current: ProductDescriptor;
  promise: undefined;
};
function getProductDescriptor(
  productCode: string,
  priceReference: string,
  params?: { fetch?: boolean; forceFetchBasicData?: boolean },
) {
  const fetch = params?.fetch ?? false;
  // request needs to run first, so selectProductDescriptor will get P&A in loading state
  const promise: Promise<ProductDescriptor> | undefined = fetch
    ? getStore().dispatch(requestFullProductData(productCode, priceReference, params))
    : undefined;
  return {
    current: selectProductDescriptor(getStore().getState(), { productCode, priceReference }),
    promise,
  };
}

function getBaseProductData(productCode: string): ProductData | null {
  return getStore().getState().products.basic[productCode];
}

interface UseProductDescriptorProps {
  productCode: string;
  priceReference: string;
  options?: {
    /**
     * Request data if not present
     */
    fetch?: boolean;
    fetchPrice?: boolean;
    fetchAvailability?: boolean;
    forceFetchBasicData?: boolean;
  };
}

const useNullableProductDescriptor = ({ productCode, priceReference, options = {} }: UseProductDescriptorProps) => {
  const productDescriptor = useSelector((state: RootState) => {
    if (!productCode) {
      return null;
    }
    return selectProductDescriptor(state, {
      productCode: productCode,
      priceReference: priceReference,
    });
  });

  const dispatch = useDispatch();
  useEffect(() => {
    if (productCode && options.fetch) {
      dispatch(requestFullProductData(productCode, priceReference, options));
    }
  }, [productCode, priceReference, options.fetch]);

  return productDescriptor;
};

const useProductDescriptor = ({ productCode, priceReference, options = {} }: UseProductDescriptorProps) => {
  const productDescriptor = useSelector((state: RootState) => {
    return selectProductDescriptor(state, {
      productCode: productCode,
      priceReference: priceReference,
    });
  });

  const dispatch = useDispatch();
  useEffect(() => {
    if (options.fetch) {
      dispatch(requestFullProductData(productCode, priceReference, options));
    }
  }, [productCode, priceReference, options.fetch]);

  return productDescriptor;
};

interface UseMultipleProductDescriptorsProps {
  productCodes: string[];
  priceReference: string;
  options?: {
    /**
     * Request data if not present
     */
    fetch?: boolean;
    fetchAvailability?: boolean;
  };
}

const useMultipleProductDescriptors = ({
  productCodes,
  priceReference,
  options = {},
}: UseMultipleProductDescriptorsProps) => {
  const productDescriptors = useSelector((state: RootState) => {
    return productCodes.map((productCode) =>
      selectProductDescriptor(state, {
        productCode: productCode,
        priceReference: priceReference,
      }),
    );
  }, shallowEqual).filter((_) => _.base);

  const dispatch = useDispatch();
  useEffect(() => {
    if (options.fetch) {
      productCodes.forEach((productCode) => dispatch(requestFullProductData(productCode, priceReference, options)));
    }
  }, [productCodes.join("-"), priceReference, options.fetch]);

  return useMemo(() => toMapFn(productDescriptors, (_) => _.base.code), [productDescriptors]);
};

const findPackagingByCode = (productDescriptor: ProductDescriptor, packCode: string) => {
  return (
    productDescriptor.availability?.packagingInformation.find((packaging) => packaging.packCode === packCode) ??
    INVALID_PACKAGING
  );
};

const findOptionalPartById = (productDescriptor: ProductDescriptor, productId: string) => {
  return isKITProduct(productDescriptor.base)
    ? productDescriptor.base.optionalParts.find((item) => item.id === productId)
    : undefined;
};

const EMPTY_WAREHOUSE: WarehouseStockData = {
  code: "",
  name: "-",
  inClarification: 0,
  inProduction: [],
  inProductionWeekly: [],
  inStock: [
    {
      quantity: 0,
    },
  ],
  inStockWeekly: [],
};

const INVALID_PACKAGING: PackagingInformationEntry = {
  packCode: INVALID_PACKAGE_CODE,
  primaryWarehouseCode: "",
  primaryWarehouseName: "",
  warehouses: [EMPTY_WAREHOUSE],
  global: EMPTY_WAREHOUSE,
  totalWarehouse: EMPTY_WAREHOUSE,
  packSize: 1,
  preferredPackQuantity: null,
  replenishmentLeadTimeDate: null,
  minOrderQuantity: 1,
};

export const ProductService = {
  getProductDescriptor,
  getBaseProductData,
  useProductDescriptor,
  useNullableProductDescriptor,
  useMultipleProductDescriptors,
  findPackagingByCode,
  findOptionalPartById,
} as const;
